import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilePlus2, LoaderCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/use-auth";
import { groupKeys } from "@/features/groups/group-queries";
import { useFirebase } from "@/hooks/use-firebase";
import {
  createPage,
  pageActivityActor,
} from "@/services/page-service";
import type { DocumentationPage } from "@/types/domain";

export function CreatePageButton({
  groupId,
  parentPageId = null,
  label = "New page",
  variant = "default",
  size = "default",
}: {
  groupId: string;
  parentPageId?: string | null;
  label?: string;
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "icon";
}) {
  const { user } = useAuth();
  const { db } = useFirebase();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () =>
      createPage(db, groupId, pageActivityActor(user!), {
        title: "Untitled",
        parentPageId,
      }),
    onSuccess: (page) => {
      const key = groupKeys.pages(groupId);
      queryClient.setQueryData<DocumentationPage[]>(key, (current = []) =>
        [...current.filter((item) => item.id !== page.id), page].sort(
          (a, b) => a.order - b.order,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: key });
      void navigate(`/app/groups/${groupId}/pages/${page.id}`);
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? (
        <LoaderCircle className="size-4 animate-spin" />
      ) : (
        <FilePlus2 className="size-4" />
      )}
      {size !== "icon" && label}
    </Button>
  );
}
